<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import LitepieDatepicker from 'litepie-datepicker-tw3'

import type { AudienceRule, RuleValue } from '@/types/types'
const { t } = useI18n()
const { formatFromArrayToDatePicker, formatDatePickerToStringArray } = useDate()
const { basicDateOperators, relativeOperators, determineRuleType } = useAudience()

interface DateComparisonProps {
  rule: AudienceRule
  label?: string
  disabled: boolean
  isRelativeRule: boolean
  useDirectRule?: boolean
}

const props = withDefaults(defineProps<DateComparisonProps>(), {
  isRelativeRule: false,
  useDirectRule: false,
  disabled: false,
  label: ''
})

const emit = defineEmits(['update:rule'])

const audienceRule = useVModel(props, 'rule', emit)
const isCalendarVisible = ref(false)
const datePickerRef = ref(null)
const lastSelectedOperator = ref(0)

const availableOperators = computed(() =>
  props.isRelativeRule ? mapOperators(relativeOperators.value) : mapOperators(basicDateOperators.value)
)

const currentRule = computed<{ type: string; operator: string; value: RuleValue }>({
  get: () => {
    const rule = getCurrentRule()
    if (!rule.type) {
      rule.type = determineRuleType(rule.value)
    }
    return rule
  },
  set: setCurrentRule
})

const selectedDate = ref(formatFromArrayToDatePicker(currentRule.value.value as string[]))

const selectedOperatorId = computed(getSelectedOperatorId)

const isRelativeRuleType = computed(() => props.isRelativeRule && currentRule.value.type === 'relative')

function mapOperators(operators) {
  return operators.map((operator) => ({
    id: operator.id,
    text: props.isRelativeRule ? operator.text : t(`pages.audience.operators.date.${operator.value}`),
    disabled: operator.disabled ?? false,
    value: operator.value,
    color: operator.color
  }))
}

function getCurrentRule() {
  if (props.useDirectRule) {
    return audienceRule.value as { type: string; operator: string; value: RuleValue }
  }
  if (props.isRelativeRule && audienceRule.value.parameters?.date_filter) {
    return audienceRule.value.parameters.date_filter as { type: string; operator: string; value: RuleValue }
  }
  return audienceRule.value as { type: string; operator: string; value: RuleValue }
}

function setCurrentRule(value: { type: string; operator: string; value: RuleValue }) {
  if (props.useDirectRule) {
    audienceRule.value = value as AudienceRule
  } else if (props.isRelativeRule) {
    audienceRule.value = {
      ...audienceRule.value,
      parameters: {
        ...audienceRule.value.parameters,
        date_filter: value
      }
    }
  } else {
    audienceRule.value = value as AudienceRule
  }
}

function getSelectedOperatorId() {
  if (!currentRule.value.operator) return 0

  const operator = availableOperators.value.find(
    (op) =>
      op.value === currentRule.value.operator &&
      (currentRule.value.type !== 'relative' || [7, 8, 9, 10, 11].includes(op.id))
  )
  return operator?.id ?? 0
}

function updateDate() {
  if (Array.isArray(selectedDate.value)) {
    currentRule.value.value = selectedDate.value.map(formatDatePickerToStringArray)
  }
}

function handleOperatorSelection(operatorId: number) {
  lastSelectedOperator.value = operatorId
  const selectedOperator = availableOperators.value.find((op) => op.id === operatorId)
  if (selectedOperator) {
    currentRule.value.type = [7, 8, 9, 10, 11].includes(operatorId) ? 'relative' : 'absolute'
    clearDate()
    currentRule.value.operator = selectedOperator.value
  }
}

function capitalizeFirstLetter(str: string): string {
  return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : ''
}

function clearDate() {
  if (datePickerRef.value?.clearPicker) {
    datePickerRef.value.clearPicker()
  }
  currentRule.value.value = []
}

watch(() => selectedDate.value, updateDate)

onMounted(() => {
  if (props.useDirectRule && !currentRule.value.type) {
    currentRule.value.type = determineRuleType(currentRule.value.value)
  }
})
</script>

<template>
  <div
    v-if="audienceRule"
    v-bind="$attrs"
    class="flex flex-col items-center space-y-4 sm:grid sm:grid-cols-2 sm:gap-4 md:space-y-0"
  >
    <div class="sm:col-span-1">
      <ComboboxSelect
        value-key="id"
        label-key="text"
        trigger-class="w-full"
        :disabled="disabled"
        :model-value="selectedOperatorId"
        :placeholder="t('pages.audience.operators.date.placeholder')"
        :options="availableOperators"
        @update:model-value="handleOperatorSelection"
      />
    </div>
    <div
      v-if="!isRelativeRule || useDirectRule || (isRelativeRule && currentRule.operator)"
      :class="['sm:col-span-1', isRelativeRule && !useDirectRule && 'mx-1']"
    >
      <LitepieDatepicker
        v-if="!isRelativeRuleType && currentRule.type !== 'relative'"
        ref="datePickerRef"
        v-model="selectedDate"
        :disabled="disabled || !currentRule.operator"
        :class="[
          disabled && 'border-border! bg-muted! text-foreground-disabled!',
          'min-h-full! rounded-control! border-input!'
        ]"
        class="flex min-h-full! rounded-control! border-input!"
        as-single
        :use-range="currentRule.operator === 'between'"
        locale="fr"
        :formatter="{
          date: 'DD/MM/YYYY',
          month: 'MMM'
        }"
        @click="isCalendarVisible = !isCalendarVisible"
      />
      <AppFormInput
        v-else
        id="date-picker-input"
        :model-value="Array.isArray(currentRule.value) ? currentRule.value[0] : currentRule.value"
        class="flex h-full"
        :disabled="disabled"
        type="number"
        :min="0"
        @update:model-value="(value) => (currentRule.value = value ? [value] : [])"
      >
        <template #icon-right>
          <span class="text-sm text-muted-foreground">
            {{ capitalizeFirstLetter(t('global.words.days')) }}
          </span>
        </template>
      </AppFormInput>
    </div>
  </div>
</template>
